import config from '../../../../config';
import {
  formatError,
  getLocalStorageItem,
  Logger,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../../packages/logger/src';
import { ERROR_RESPONSE_TEXT, WEBEX_STORAGE_KEYS } from '../../../adapter-interfaces/src';
import { isFedRampEnvironment } from '../utils/environmentTypes';

const webexInstance = require('../../../../packages/widgets/src/webex-js-sdk/webex');

// Try to import DSS plugin - will register if Webex SDK supports it
let DssPlugin: any = undefined;
try {
  DssPlugin = require('@webex/internal-plugin-dss').default;
  console.log('[Enrichment] DSS plugin imported successfully');
} catch (err) {
  console.error('[Enrichment] Failed to import DSS plugin:', err);
}

// Try to import Avatar plugin - will register so webex.internal.avatar is available
let AvatarPlugin: any = undefined;
try {
  AvatarPlugin = require('@webex/internal-plugin-avatar').default;
  console.log('[Enrichment] Avatar plugin imported successfully');
} catch (err) {
  console.error('[Enrichment] Failed to import Avatar plugin:', err);
}

// Try to import AI Summary plugin - will register so webex.internal.aisummary is available
let AISummaryPlugin: any = undefined;
try {
  AISummaryPlugin = require('@webex/internal-plugin-call-ai-summary').default;
  console.log('[AISummary] AI Summary plugin imported successfully');
} catch (err) {
  console.error('[AISummary] Failed to import AI Summary plugin:', err);
}

// Default config required by @webex/internal-plugin-avatar (cacheControl, defaultAvatarSize, sizes)
const AVATAR_PLUGIN_DEFAULT_CONFIG = {
  cacheControl: 60 * 60,
  defaultAvatarSize: 80,
  sizes: [40, 50, 80, 110, 135, 192, 640, 1600],
  batcherWait: 100,
  batcherMaxCalls: 100,
  batcherMaxWait: 1500,
  thumbnailMaxHeight: 960,
  thumbnailMaxWidth: 640,
};
let retryRegisterCount: number = 0;
const MAX_RETRY_COUNT = 5;
let initInprogress: boolean = false;
declare global {
  interface Window {
    webex: any;
    isWebexInitalized: boolean;
  }
}

window.isWebexInitalized = false;

/** DSS batch lookups require `webex.config.dss.requestTimeout`; missing it breaks lookupByPhoneNumbers in some embed contexts. */
const WEBEX_DSS_REQUEST_TIMEOUT_MS = 30000;

/** Ensure `webex.config.dss.requestTimeout` exists after init or manual plugin attach. */
function ensureWebexDssConfig(): void {
  const cfg = window.webex?.config;
  if (!cfg) {
    return;
  }
  if (!cfg.dss || typeof cfg.dss !== 'object') {
    cfg.dss = { requestTimeout: WEBEX_DSS_REQUEST_TIMEOUT_MS };
    return;
  }
  if (cfg.dss.requestTimeout == null) {
    cfg.dss.requestTimeout = WEBEX_DSS_REQUEST_TIMEOUT_MS;
  }
}

// FedRAMP and FedRAMP-High (GCC-High) must both light up the SDK's FedRAMP
const IS_FEDRAMP_ENVIRONMENT = isFedRampEnvironment(config.APP_ENVIRONMENT);

const webexConfig = {
  fedramp: IS_FEDRAMP_ENVIRONMENT,
  config: {
    fedramp: IS_FEDRAMP_ENVIRONMENT,
    requestTimeout: WEBEX_DSS_REQUEST_TIMEOUT_MS,
    // @webex/internal-plugin-dss reads webex.config.dss.requestTimeout
    dss: {
      requestTimeout: WEBEX_DSS_REQUEST_TIMEOUT_MS,
    },
    services: {
      discovery: {
        u2c: config.WEBEX_U2C_BASE_URL,
        hydra: config.HYDRA_SERVICE_URL,
      },
    },
    // Any other sdk config we need
  },
  credentials: {
    access_token: getLocalStorageItem<string>("webex_auth_token"),
  },
  logger: {
    level: process.env.WEBEX_LOG_LEVEL
  }
};

export function initWebex(accessToken: string) {
  Logger.info(
    'Authentication#initWebex()'
  );
  if (initInprogress) {
    Logger.info(
      'Authentication#initWebex() :: webex init already in-progress'
    );
    return;
  }

  initInprogress = true;

  if (accessToken) {
    webexConfig.credentials.access_token = accessToken;
  }
  window.webex = webexInstance?.init(webexConfig);
  if (window.webex?.config && window.webex.config.requestTimeout == null) {
    window.webex.config.requestTimeout = WEBEX_DSS_REQUEST_TIMEOUT_MS;
  }
  ensureWebexDssConfig();
  // Avatar plugin reads config from webex.config.avatar (derived in WebexPlugin). Set defaults so retrieveAvatarUrl does not read undefined.
  if (window.webex?.config) {
    window.webex.config.avatar = window.webex.config.avatar
      ? { ...AVATAR_PLUGIN_DEFAULT_CONFIG, ...window.webex.config.avatar }
      : AVATAR_PLUGIN_DEFAULT_CONFIG;
  }
  window.webex?.once('ready', async () => {
    Logger.info(
      `Authentication#initWebex() :: Webex Ready version: ${window.webex?.version}`
    );
    
    // Manually attach DSS plugin if it wasn't registered during import
    if (DssPlugin && window.webex?.internal && !window.webex.internal.dss) {
      try {
        console.log('[Enrichment] Attempting to manually register DSS plugin...');
        // Try to manually instantiate and attach DSS plugin
        window.webex.internal.dss = new DssPlugin(window.webex, {
          config: { requestTimeout: WEBEX_DSS_REQUEST_TIMEOUT_MS },
        });
        if (window.webex.internal.dss && !(window.webex.internal.dss as any).parent) {
          (window.webex.internal.dss as any).parent = window.webex;
        }
        ensureWebexDssConfig();
        console.log('[Enrichment] DSS plugin manually attached');
      } catch (err) {
        console.error('[Enrichment] Failed to manually attach DSS plugin:', err);
      }
    }

    ensureWebexDssConfig();
    // Manually attach Avatar plugin if it wasn't registered during import
    if (AvatarPlugin && window.webex?.internal && !window.webex.internal.avatar) {
      try {
        console.log('[Enrichment] Attempting to manually register Avatar plugin...');
        // Plugin config is derived from webex.config.avatar (see WebexPlugin derived config); we set it above before init.
        window.webex.internal.avatar = new AvatarPlugin(window.webex, {});
        if (window.webex.internal.avatar && !(window.webex.internal.avatar as any).parent) {
          (window.webex.internal.avatar as any).parent = window.webex;
        }
        console.log('[Enrichment] Avatar plugin manually attached');
      } catch (err) {
        console.error('[Enrichment] Failed to manually attach Avatar plugin:', err);
      }
    }

    // Manually attach AI Summary plugin if it wasn't registered during import
    if (AISummaryPlugin && window.webex?.internal && !window.webex.internal.aisummary) {
      try {
        console.log('[AISummary] Attempting to manually register AI Summary plugin...');
        window.webex.internal.aisummary = new AISummaryPlugin(window.webex, {});
        if (window.webex.internal.aisummary && !(window.webex.internal.aisummary as any).parent) {
          (window.webex.internal.aisummary as any).parent = window.webex;
        }
        console.log('[AISummary] AI Summary plugin manually attached');
      } catch (err) {
        console.error('[AISummary] Failed to manually attach AI Summary plugin:', err);
      }
    }

    // Log DSS availability for phone enrichment debugging
    const hasDss = Boolean(window.webex?.internal?.dss);
    const dssKeys = hasDss ? Object.keys(window.webex.internal.dss) : [];
    console.log('[Enrichment] Webex initialized - DSS status:', {
      hasDss,
      hasInternal: Boolean(window.webex?.internal),
      internalPlugins: window.webex?.internal ? Object.keys(window.webex.internal).slice(0, 20) : [],
      dssKeys,
      hasLookupMethod: hasDss ? typeof window.webex.internal.dss.lookupByPhoneNumbers : 'N/A'
    });

    // Log AI Summary plugin availability for debugging
    const hasAISummary = Boolean(window.webex?.internal?.aisummary);
    const aiSummaryKeys = hasAISummary ? Object.keys(window.webex.internal.aisummary).slice(0, 10) : [];
    console.log('[AISummary] Webex initialized - AI Summary plugin status:', {
      hasAISummary,
      hasInternal: Boolean(window.webex?.internal),
      aiSummaryKeys,
      hasMethods: hasAISummary ? {
        getContainer: typeof window.webex.internal.aisummary.getContainer,
        getSummary: typeof window.webex.internal.aisummary.getSummary,
        getTranscript: typeof window.webex.internal.aisummary.getTranscript
      } : 'N/A'
    });

    // Mark webex as initialized before device registration so contacts init doesn't wait unnecessarily
    window.isWebexInitalized = true;
    
    await deviceRegister();
  });
  initInprogress = false;
}

async function deviceRegister() {
  Logger.info(
    'Authentication#deviceRegister() :: initializing start.'
  );

  if (retryRegisterCount >= MAX_RETRY_COUNT) {
    setLocalStorageItem(WEBEX_STORAGE_KEYS.WEBEX_INSTANCE_ERROR, ERROR_RESPONSE_TEXT.WEBEX_INST_ERROR);
    window.dispatchEvent(new Event("storage"));
    Logger.error(`Authentication#deviceRegister() :: Max retry exceeded.`);
    return;
  }
  if (window.webex?.internal?.device?.registered) {
    //TODO:: do we need to reconnect mercury
    Logger.info(
      'Authentication#deviceRegister() :: already registered.'
    );
    return;
    //unregister()
  }
  retryRegisterCount++;
  await window.webex?.internal?.device?.register()
    .then(async () => {
      Logger.info(
        'Authentication#deviceRegister() :: successfully registered.'
      );
      await mercuryConnect();
      
      // Initialize contacts for phone enrichment AFTER device registration
      const userId = window.webex?.internal?.device?.userId;
      if (userId && window.webex) {
        try {
          const { initializeContactsForEnrichment } = await import('../../../../services/phoneEnrichment/contactsIntegration');
          // Run contacts init in background to avoid blocking call history/UI on cold cache.
          void initializeContactsForEnrichment(window.webex, userId).catch((initError: unknown) => {
            console.error('[Enrichment] Contacts init background failure:', initError);
          });
        } catch (error) {
          console.error('[Enrichment] Failed to initialize contacts:', error);
        }
      }
    })
    .catch((error: any) => {
      Logger.error(`Authentication#deviceRegister() :: error registering: '${formatError(error)}'.`);
      if (window.webex?.internal?.device?.registered) {
        deviceRefresh();
      } else {
        deviceUnregister();
      }
    })
    .finally(() => {
      if (!(window.webex?.internal?.device?.registered)) {
        deviceRegister()
      } else {
        //SUCCESS :: removing the error warning on webex initalization
        removeLocalStorageItem(WEBEX_STORAGE_KEYS.WEBEX_INSTANCE_ERROR);
        window.dispatchEvent(new Event("storage"));
        retryRegisterCount = 0;
      }

    });
}

async function mercuryConnect() {
  if (window.webex?.internal?.mercury?.connected) {
    Logger.info(
      'Authentication#mercuryConnect() :: May need to disconnect'
    );
    return;
    //TODO:: do we need to reconnect
  }

  await window.webex?.internal?.mercury?.connect()
    .then(() => {
      Logger.info(
        'Authentication#mercuryConnect() :: successfully registered'
      );

    })
    .catch((error: any) => {
      Logger.error(`Authentication#mercuryConnect() :: error registering: '${formatError(error)}'.`);
      if (window.webex?.internal?.mercury?.connected) {
        Logger.error(`Authentication#mercuryConnect() :: may need to reconnect: `);
        //unregister()
      }
    })
    .finally(() => {
      if (!(window.webex?.internal?.mercury?.connected)) {
        Logger.error(`Authentication#mercuryConnect() :: may need to retry finally:`);
        //unregister()
      }

    });
}

async function deviceUnregister() {
  Logger.info(
    'Authentication#deviceUnregister()'
  );
  await window.webex?.internal?.device?.unregister()
    .then(() => {
      Logger.info(
        'Authentication#device#deviceUnregister() :: successfully unregistered'
      );

    })
    .catch((error: any) => {
      Logger.error(`Authentication#device#deviceUnregister() :: error unregistering: '${formatError(error)}'.`);
    })
    .finally(() => {
      Logger.info(
        `Authentication#deviceUnregister() :: is device registered ${window.webex?.internal?.device?.registered}`
      );
    });
}

async function deviceRefresh() {
  Logger.info(
    'Authentication#deviceRefresh()'
  );
  if (window.webex?.internal?.device?.registered) {
    //register()
    await window.webex?.internal?.device?.refresh()
      .then(() => {
        Logger.info(
          `Authentication#device#deviceRefresh() :: successfully device refreshed`
        );

      })
      .catch((error: any) => {
        Logger.error(`Authentication#device#deviceRefresh() :: device refresh failed: '${formatError(error)}'.`);
      })
      .finally(() => {

      });
  }
}

/**
 * Wait until the global Webex SDK is initialized and ready.
 * Resolves with the `window.webex` instance or rejects after timeout.
 * Useful for consumers that may be started before `initWebex()` runs.
 */
export function whenWebexReady(timeoutMs: number = 20000): Promise<any> {
  const pollInterval = 500;
  const start = Date.now();

  if (window.webex && window.isWebexInitalized) {
    return Promise.resolve(window.webex);
  }

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (window.webex && window.isWebexInitalized) {
        clearInterval(timer);
        resolve(window.webex);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        reject(new Error('whenWebexReady: timeout waiting for webex initialization'));
      }
    }, pollInterval);
  });
}

/**
 * Wait until the Webex device is registered.
 * Resolves when device registration completes or rejects after timeout.
 */
export function whenDeviceRegistered(timeoutMs: number = 20000): Promise<void> {
  const pollInterval = 500;
  const start = Date.now();

  if (window.webex?.internal?.device?.registered) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (window.webex?.internal?.device?.registered) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        reject(new Error('whenDeviceRegistered: timeout waiting for device registration'));
      }
    }, pollInterval);
  });
}

/**
 * Wait until DSS plugin is initialized and ready.
 * Resolves with the DSS plugin instance or rejects after timeout.
 * Useful for phone enrichment features that depend on DSS.
 */
export function whenDssReady(timeoutMs: number = 20000): Promise<any> {
  const pollInterval = 500;
  const start = Date.now();

  if (window.webex?.internal?.dss && window.isWebexInitalized) {
    return Promise.resolve(window.webex.internal.dss);
  }

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (window.webex?.internal?.dss && window.isWebexInitalized) {
        clearInterval(timer);
        resolve(window.webex.internal.dss);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        reject(new Error('whenDssReady: timeout waiting for DSS plugin initialization'));
      }
    }, pollInterval);
  });
}
